var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { useMemo } from 'react';
import classNames from 'classnames';
import { fillRef } from 'rc-util/lib/ref';
import ColGroup from '../ColGroup';
import TableContext from '../context/TableContext';
function useColumnWidth(colWidths, columCount) {
    return useMemo(() => {
        const cloneColumns = [];
        for (let i = 0; i < columCount; i += 1) {
            const val = colWidths[i];
            if (val !== undefined) {
                cloneColumns[i] = val;
            }
            else {
                return null;
            }
        }
        return cloneColumns;
    }, [colWidths.join('_'), columCount]);
}
const FixedHolder = React.forwardRef((_a, ref) => {
    var { className, noData, columns, flattenColumns, colWidths, columCount, stickyOffsets, direction, fixHeader, stickyTopOffset, stickyBottomOffset, stickyClassName, onScroll, maxContentScroll, children } = _a, props = __rest(_a, ["className", "noData", "columns", "flattenColumns", "colWidths", "columCount", "stickyOffsets", "direction", "fixHeader", "stickyTopOffset", "stickyBottomOffset", "stickyClassName", "onScroll", "maxContentScroll", "children"]);
    const { prefixCls, scrollbarSize, isSticky } = React.useContext(TableContext);
    const combinationScrollBarSize = isSticky && !fixHeader ? 0 : scrollbarSize;
    // Pass wheel to scroll event
    const scrollRef = React.useRef(null);
    const setScrollRef = React.useCallback((element) => {
        fillRef(ref, element);
        fillRef(scrollRef, element);
    }, []);
    React.useEffect(() => {
        var _a;
        function onWheel(e) {
            const { currentTarget, deltaX } = e;
            if (deltaX) {
                onScroll({ currentTarget, scrollLeft: currentTarget.scrollLeft + deltaX });
                e.preventDefault();
            }
        }
        (_a = scrollRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('wheel', onWheel);
        return () => {
            var _a;
            (_a = scrollRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('wheel', onWheel);
        };
    }, []);
    // Check if all flattenColumns has width
    const allFlattenColumnsWithWidth = React.useMemo(() => flattenColumns.every(column => column.width >= 0), [flattenColumns]);
    // Add scrollbar column
    const lastColumn = flattenColumns[flattenColumns.length - 1];
    const ScrollBarColumn = {
        fixed: lastColumn ? lastColumn.fixed : null,
        scrollbar: true,
        onHeaderCell: () => ({
            className: `${prefixCls}-cell-scrollbar`,
        })
    };
    const columnsWithScrollbar = useMemo(() => (combinationScrollBarSize ? [...columns, ScrollBarColumn] : columns), [combinationScrollBarSize, columns]);
    const flattenColumnsWithScrollbar = useMemo(() => (combinationScrollBarSize ? [...flattenColumns, ScrollBarColumn] : flattenColumns), [combinationScrollBarSize, flattenColumns]);
    // Calculate the sticky offsets
    const headerStickyOffsets = useMemo(() => {
        const { right, left } = stickyOffsets;
        return Object.assign(Object.assign({}, stickyOffsets), { left: direction === 'rtl' ? [...left.map(width => width + combinationScrollBarSize), 0] : left, right: direction === 'rtl'
                ? right
                : [...right.map(width => width + combinationScrollBarSize), 0], isSticky });
    }, [combinationScrollBarSize, stickyOffsets, isSticky]);
    const mergedColumnWidth = useColumnWidth(colWidths, columCount);
    return (<div style={Object.assign({ overflow: 'hidden' }, (isSticky ? { top: stickyTopOffset, bottom: stickyBottomOffset } : {}))} ref={setScrollRef} className={classNames(className, {
            [stickyClassName]: !!stickyClassName,
        })}>
                <table style={{
            tableLayout: 'fixed',
            visibility: noData || mergedColumnWidth ? null : 'hidden'
        }}>
                    {(!noData || !maxContentScroll || allFlattenColumnsWithWidth) && (<ColGroup colWidths={mergedColumnWidth ? [...mergedColumnWidth, combinationScrollBarSize] : []} columCount={columCount + 1} columns={flattenColumnsWithScrollbar}/>)}
                    {children(Object.assign(Object.assign({}, props), { stickyOffsets: headerStickyOffsets, columns: columnsWithScrollbar, flattenColumns: flattenColumnsWithScrollbar }))}
                </table>
            </div>);
});
FixedHolder.displayName = 'FixedHolder';
export default FixedHolder;
